/**
 * 帧解析状态枚举
 * @enum {number}
 */
enum ParseState {
  /** 等待帧起始标志 STX */
  WAIT_STX,
  /** 等待读取长度字段 */
  WAIT_LENGTH,
  /** 正在接收帧数据 */
  READ_DATA,
  /** 等待 ETX */
  WAIT_ETX,
  /** 等待 LRC */
  WAIT_LRC,
  /** 帧接收完成 */
  COMPLETE,
}

/**
 * 帧解析结果状态枚举
 * NAK - 指令异常，需要重新发送指令
 * COMPLETE - 解析完成
 * INCOMPLETE - 帧不完整
 * TIMEOUT - 超时
 */
type FrameResultStatus =
  | "ACK"
  | "NAK"
  | "COMPLETE"
  | "TIMEOUT"
  | "EOF"
  | "INCOMPLETE"
  | "ERROR";

/**
 * 帧解析结果接口
 */
export interface FrameResult<T> {
  /** 状态 */
  status: FrameResultStatus;
  /** 状态码: 0 表示成功, 1 表示失败, -1 表示 串口断开 */
  code?: 0 | 1 | -1;
  /** 数据内容(成功时) */
  data?: T;
  /** 消息 */
  msg: string;
  payload?: Uint8Array;
  raw?: Uint8Array;
  length?: number;
  lrc?: number;
}

/**
 * 帧统计信息接口
 */
interface FrameStats {
  /** 接收的总字节数 */
  bytes: number;
  /** 接收的总帧数 */
  frames: number;
}

/**
 * 串口帧解析器类
 *
 * 实现基于状态机的串口通信帧解析,支持以下协议:
 * - 帧格式: STX + 数据 + ETX + LRC
 * - 控制字符: ACK(确认), NAK(否认)
 * - 校验方式: LRC(纵向冗余校验)
 *
 * @example
 * ```typescript
 * const parser = new FrameParser();
 * await parser.readLoop(reader, (result) => {
 *   if (result.status === 'ok') {
 *     console.log('接收到数据:', result.data);
 *   }
 * });
 * ```
 */
export class FrameParser {
  /* ==================== 常量 ==================== */
  private readonly MAX_FRAME = 4096; // 最大帧长度
  private readonly STX = 0x02; // 帧起始
  private readonly ETX = 0x03; // 帧结束
  private readonly ACK = 0x06; // 确认
  private readonly NAK = 0x15; // 否认

  /* ==================== 内部状态 ==================== */

  /** 帧数据缓冲区 */
  private buffer: Uint8Array;

  /** 缓冲区写入指针 */
  private pointer: number;

  /** 当前解析状态 */
  private state: ParseState;

  /** 统计信息 */
  private statistics: FrameStats;

  /** 长度字段缓冲区(2字节BCD) */
  private lengthBuffer: Uint8Array;

  /** 长度字段已接收字节数 */
  private lengthBytesReceived: number;

  /** 期望的数据长度(从长度字段解码得到) */
  private expectedDataLength: number;

  /** 已接收的数据字节数 */
  private dataBytesReceived: number;

  private streamBuffer: number[];

  /**
   * 构造函数
   * 初始化解析器状态和缓冲区
   */
  constructor() {
    this.buffer = new Uint8Array(this.MAX_FRAME);
    this.pointer = 0;
    this.state = ParseState.WAIT_STX;
    this.statistics = { bytes: 0, frames: 0 };
    this.lengthBuffer = new Uint8Array(2);
    this.lengthBytesReceived = 0;
    this.expectedDataLength = 0;
    this.dataBytesReceived = 0;
    this.streamBuffer = [];
  }

  /* ==================== 私有方法 ==================== */

  /**
   * 重置解析器状态
   * 将状态机重置为初始状态,清空缓冲区指针
   *
   * @private
   */
  public resetState(): void {
    this.state = ParseState.WAIT_STX;
    this.pointer = 0;
    this.lengthBytesReceived = 0;
    this.expectedDataLength = 0;
    this.dataBytesReceived = 0;
    this.streamBuffer = [];
  }

  /**
   * BCD转字符串
   * @private
   * @param {Uint8Array} bcdBytes - BCD编码的字节数组
   * @returns {string} 解码后的字符串
   * @throws {Error} 当BCD字节包含非法值(A-F)时抛出错误
   */
  private bcd2Str(bcdBytes: Uint8Array): string {
    let result = "";
    for (const byte of bcdBytes) {
      const high = (byte >> 4) & 0x0f;
      const low = byte & 0x0f;

      // 验证BCD有效性: 每个半字节必须在0-9范围内
      if (high > 9 || low > 9) {
        throw new Error("INVALID_BCD_DIGIT");
      }

      result += high.toString() + low.toString();
    }
    return result;
  }

  public push(chunk: Uint8Array): FrameResult<any>[] {
    const out: FrameResult<any>[] = [];
    if (!chunk || chunk.length === 0) return out;
    let offset = 0;
    while (offset < chunk.length) {
      const b0 = chunk[offset];
      if (b0 === this.ACK) {
        if (this.streamBuffer.length === 0) {
          this.resetState();
          out.push({
            status: "ACK",
            code: 0,
            msg: "已收到应答,请等待数据返回",
          });
          offset++;
          continue;
        }
      }
      if (b0 === this.NAK) {
        if (this.streamBuffer.length === 0) {
          this.resetState();
          out.push({ status: "NAK", code: 1, msg: "请求被拒绝" });
          offset++;
          continue;
        }
      }
      if (this.streamBuffer.length === 0 && b0 !== this.STX) {
        out.push({ status: "ERROR", code: 1, msg: "stx_not_found" });
        const nextStx = (chunk as any).indexOf
          ? (chunk as any).indexOf(this.STX, offset + 1)
          : -1;
        if (nextStx === -1) {
          return out;
        } else {
          offset = nextStx;
        }
      }
      for (let i = offset; i < chunk.length; i++)
        this.streamBuffer.push(chunk[i]);
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (this.streamBuffer.length < 5) break;
        let length = 0;
        try {
          const lenBytes = Uint8Array.from(this.streamBuffer.slice(1, 3));
          const lengthStr = this.bcd2Str(lenBytes);
          length = parseInt(lengthStr, 10);
        } catch {
          out.push({ status: "ERROR", code: 1, msg: "len_format" });
          this.resetState();
          break;
        }
        if (!Number.isFinite(length)) {
          out.push({ status: "ERROR", code: 1, msg: "len_format" });
          this.resetState();
          break;
        }
        const total = 5 + length;
        if (this.streamBuffer.length < total) break;
        const buf = Uint8Array.from(this.streamBuffer.slice(0, total));
        const etxIndex = 3 + length;
        if (buf[etxIndex] !== this.ETX) {
          out.push({ status: "ERROR", code: 1, msg: "etx_not_found" });
          this.resetState();
          break;
        }
        const olrcIndex = 4 + length;
        const olrc = buf[olrcIndex];
        const lrcMsg = buf.slice(1, etxIndex + 1);
        const nlrc = calcLrc(lrcMsg);
        if (nlrc !== olrc) {
          out.push({ status: "ERROR", code: 1, msg: "lrc_mismatch" });
          this.resetState();
          break;
        }
        const payload = buf.slice(3, 3 + length);
        let jsonData: any | undefined;
        if (payload.length > 0) {
          const text = new TextDecoder().decode(payload).trim();
          try {
            jsonData = JSON.parse(text);
            // eslint-disable-next-line no-empty
          } catch {}
        }
        out.push({
          status: "COMPLETE",
          code: 0,
          data: jsonData,
          msg: "成功",
          payload,
          raw: buf,
          length,
          lrc: olrc,
        });
        this.statistics.frames++;
        this.streamBuffer = this.streamBuffer.slice(total);
      }
      break;
    }
    return out;
  }

  /* ==================== 公共方法 ==================== */

  /**
   * 获取当前统计信息
   *
   * @returns {Readonly<FrameStats>} 只读的统计信息对象
   */
  public getStats(): Readonly<FrameStats> {
    return { ...this.statistics };
  }

  /**
   * 重置统计信息
   */
  public resetStats(): void {
    this.statistics = { bytes: 0, frames: 0 };
  }

  /**
   * 主循环: 从流中读取数据并解析帧
   *
   */
  public async parseChunk<T>(value: Uint8Array): Promise<FrameResult<T>[]> {
    try {
      this.statistics.bytes += value.length;
      const events = this.push(value);
      if (events.length === 0) {
        return [] as FrameResult<T>[];
      }
      return events as FrameResult<T>[];
    } catch (error: any) {
      this.resetState();
      return [
        {
          status: "ERROR",
          code: 1,
          msg: error.message || "解析失败",
        } as FrameResult<T>,
      ];
    }
  }
}

// 转成bcd格式
const str2Bcd = (s: string) => {
  // console.log("Execute str2Bcd!");

  if (s.length % 2 !== 0) {
    s = "0" + s;
  }
  const baos = new Uint8Array(Math.floor(s.length / 2));
  const cs = s.split("");
  for (let i = 0; i < cs.length; i += 2) {
    const h = cs[i].charCodeAt(0) - 48;
    const l = cs[i + 1].charCodeAt(0) - 48;
    baos[i / 2] = (h << 4) | l;
  }
  return baos;
};

const calcLrc = (byteArray: Uint8Array) => {
  let lrc = 0;

  for (let i = 0; i < byteArray.length; i++) {
    lrc ^= byteArray[i];
  }

  return lrc;
};
export const createReqFromMsg = (msg: string) => {
  const encoder = new TextEncoder();
  const msgBytes = encoder.encode(msg);

  // 确保长度为4位BCD，不足补零
  const lengthStr = msgBytes.length.toString().padStart(4, "0");
  const lengthBytes = str2Bcd(lengthStr);

  // 计算LRC
  const tempBuffer = new Uint8Array(2 + msgBytes.length + 1);
  tempBuffer.set(lengthBytes, 0);
  tempBuffer.set(msgBytes, 2);
  tempBuffer[2 + msgBytes.length] = 0x03; // ETX

  const lrc = calcLrc(tempBuffer);

  // 组装完整消息
  const finalBuffer = new Uint8Array(1 + 2 + msgBytes.length + 1 + 1);
  finalBuffer[0] = 0x02; // STX
  finalBuffer.set(lengthBytes, 1);
  finalBuffer.set(msgBytes, 3);
  finalBuffer[3 + msgBytes.length] = 0x03; // ETX
  finalBuffer[4 + msgBytes.length] = lrc;

  return finalBuffer;
};
